import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

import Icon from '../../components/AppIcon';

const InfluencerProfileCreation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    socialAccounts: {
      instagram: { username: '', followers: '', verified: false },
      youtube: { username: '', followers: '', verified: false },
      tiktok: { username: '', followers: '', verified: false },
      twitter: { username: '', followers: '', verified: false }
    },
    pricing: {
      video: '',
      story: '',
      post: '',
      reel: ''
    },
    contentCategories: [],
    deliveryTime: '',
    portfolioFiles: []
  });

  const [bioCharCount, setBioCharCount] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const maxBioLength = 500;

  const contentCategories = [
    { value: 'music', label: 'Müzik' },
    { value: 'lifestyle', label: 'Yaşam Tarzı' },
    { value: 'fashion', label: 'Moda' },
    { value: 'entertainment', label: 'Eğlence' },
    { value: 'tech', label: 'Teknoloji' },
    { value: 'gaming', label: 'Oyun' },
    { value: 'food', label: 'Yemek' },
    { value: 'travel', label: 'Seyahat' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'beauty', label: 'Güzellik' }
  ];

  const deliveryTimeOptions = [
    { value: '24h', label: '24 Saat' },
    { value: '48h', label: '48 Saat' },
    { value: '3-days', label: '3 Gün' },
    { value: '1-week', label: '1 Hafta' },
    { value: '2-weeks', label: '2 Hafta' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    calculateCompletion();
  };

  const handleSocialAccountChange = (platform, field, value) => {
    setFormData(prev => ({
      ...prev,
      socialAccounts: {
        ...prev?.socialAccounts,
        [platform]: {
          ...prev?.socialAccounts?.[platform],
          [field]: value
        }
      }
    }));
    calculateCompletion();
  };

  const handlePricingChange = (contentType, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev?.pricing,
        [contentType]: value
      }
    }));
    calculateCompletion();
  };

  const handleBioChange = (e) => {
    const value = e?.target?.value || '';
    if (value?.length <= maxBioLength) {
      setBioCharCount(value?.length);
      handleInputChange('bio', value);
    }
  };

  const calculateCompletion = () => {
    const fields = [
      formData?.displayName,
      formData?.bio,
      formData?.contentCategories?.length > 0,
      formData?.pricing?.video || formData?.pricing?.story || formData?.pricing?.post,
      formData?.deliveryTime,
      Object.values(formData?.socialAccounts)?.some(acc => acc?.username)
    ];
    const completed = fields?.filter(Boolean)?.length;
    const percentage = Math.round((completed / fields?.length) * 100);
    setCompletionPercentage(percentage);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Influencer Profile Data:', formData);
    navigate('/influencer-dashboard');
  };

  const calculateEngagementRate = (followers) => {
    if (!followers || followers < 1000) return '0.0';
    const rate = (Math.random() * 5 + 3)?.toFixed(1);
    return rate;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="influencer" />
      
      <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm">Geri Dön</span>
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Influencer Profili Oluştur
              </h1>
              <p className="text-muted-foreground">
                Sosyal medya hesaplarınızı bağlayın ve hizmet fiyatlarınızı belirleyin
              </p>
            </div>
            
            <div className="hidden md:flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3">
              <div className="relative w-16 h-16">
                <svg className="transform -rotate-90 w-16 h-16">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="var(--color-muted)"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="var(--color-secondary)"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - completionPercentage / 100)}`}
                    className="transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-semibold">{completionPercentage}%</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium">Tamamlanma</p>
                <p className="text-xs text-muted-foreground">Profil Durumu</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="User" size={20} color="var(--color-secondary)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Temel Bilgiler</h2>
                <p className="text-sm text-muted-foreground">Görünen adınız ve biyografiniz</p>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Görünen Ad"
                placeholder="Örn: Ayşe Yılmaz"
                value={formData?.displayName}
                onChange={(e) => handleInputChange('displayName', e?.target?.value)}
                required
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Profesyonel Biyografi
                  <span className="text-destructive ml-1">*</span>
                </label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="İçerik tarzınızı, uzmanlık alanlarınızı ve deneyiminizi anlatın..."
                  value={formData?.bio}
                  onChange={handleBioChange}
                  required
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Kendinizi tanıtan kısa bir açıklama yazın</span>
                  <span className={bioCharCount >= maxBioLength ? 'text-error' : ''}>
                    {bioCharCount} / {maxBioLength}
                  </span>
                </div>
              </div>

              <Select
                label="İçerik Kategorileri"
                placeholder="Uzmanlık alanlarınızı seçin"
                options={contentCategories}
                value={formData?.contentCategories}
                onChange={(value) => handleInputChange('contentCategories', value)}
                multiple
                searchable
                description="Birden fazla kategori seçebilirsiniz"
                required
              />
            </div>
          </div>

          {/* Social Media Accounts */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Share2" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Sosyal Medya Hesapları</h2>
                <p className="text-sm text-muted-foreground">Hesaplarınızı bağlayın ve takipçi sayılarınızı doğrulayın</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Instagram */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Instagram" size={20} color="#E4405F" />
                  <h3 className="font-semibold">Instagram</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Kullanıcı Adı"
                    placeholder="@kullaniciadi"
                    value={formData?.socialAccounts?.instagram?.username}
                    onChange={(e) => handleSocialAccountChange('instagram', 'username', e?.target?.value)}
                  />
                  <Input
                    label="Takipçi Sayısı"
                    type="number"
                    placeholder="Örn: 50000"
                    value={formData?.socialAccounts?.instagram?.followers}
                    onChange={(e) => handleSocialAccountChange('instagram', 'followers', e?.target?.value)}
                  />
                </div>
                {formData?.socialAccounts?.instagram?.followers && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="TrendingUp" size={16} />
                    <span>Tahmini Etkileşim Oranı: %{calculateEngagementRate(formData?.socialAccounts?.instagram?.followers)}</span>
                  </div>
                )}
              </div>

              {/* YouTube */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Youtube" size={20} color="#FF0000" />
                  <h3 className="font-semibold">YouTube</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Kanal Adı"
                    placeholder="@kanaladı"
                    value={formData?.socialAccounts?.youtube?.username}
                    onChange={(e) => handleSocialAccountChange('youtube', 'username', e?.target?.value)}
                  />
                  <Input
                    label="Abone Sayısı"
                    type="number"
                    placeholder="Örn: 100000"
                    value={formData?.socialAccounts?.youtube?.followers}
                    onChange={(e) => handleSocialAccountChange('youtube', 'followers', e?.target?.value)}
                  />
                </div>
                {formData?.socialAccounts?.youtube?.followers && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="TrendingUp" size={16} />
                    <span>Tahmini Etkileşim Oranı: %{calculateEngagementRate(formData?.socialAccounts?.youtube?.followers)}</span>
                  </div>
                )}
              </div>

              {/* TikTok */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Music" size={20} color="#000000" />
                  <h3 className="font-semibold">TikTok</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Kullanıcı Adı"
                    placeholder="@kullaniciadi"
                    value={formData?.socialAccounts?.tiktok?.username}
                    onChange={(e) => handleSocialAccountChange('tiktok', 'username', e?.target?.value)}
                  />
                  <Input
                    label="Takipçi Sayısı"
                    type="number"
                    placeholder="Örn: 200000"
                    value={formData?.socialAccounts?.tiktok?.followers}
                    onChange={(e) => handleSocialAccountChange('tiktok', 'followers', e?.target?.value)}
                  />
                </div>
                {formData?.socialAccounts?.tiktok?.followers && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="TrendingUp" size={16} />
                    <span>Tahmini Etkileşim Oranı: %{calculateEngagementRate(formData?.socialAccounts?.tiktok?.followers)}</span>
                  </div>
                )}
              </div>

              {/* Twitter */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Twitter" size={20} color="#1DA1F2" />
                  <h3 className="font-semibold">Twitter / X</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Kullanıcı Adı"
                    placeholder="@kullaniciadi"
                    value={formData?.socialAccounts?.twitter?.username}
                    onChange={(e) => handleSocialAccountChange('twitter', 'username', e?.target?.value)}
                  />
                  <Input
                    label="Takipçi Sayısı"
                    type="number"
                    placeholder="Örn: 30000"
                    value={formData?.socialAccounts?.twitter?.followers}
                    onChange={(e) => handleSocialAccountChange('twitter', 'followers', e?.target?.value)}
                  />
                </div>
                {formData?.socialAccounts?.twitter?.followers && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="TrendingUp" size={16} />
                    <span>Tahmini Etkileşim Oranı: %{calculateEngagementRate(formData?.socialAccounts?.twitter?.followers)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Service Pricing */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={20} color="var(--color-success)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Hizmet Fiyatlandırması</h2>
                <p className="text-sm text-muted-foreground">Farklı içerik türleri için fiyatlarınızı belirleyin (₺)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Video" size={18} color="var(--color-primary)" />
                  <h3 className="font-semibold text-sm">Video İçeriği</h3>
                </div>
                <Input
                  type="number"
                  placeholder="Örn: 5000"
                  value={formData?.pricing?.video}
                  onChange={(e) => handlePricingChange('video', e?.target?.value)}
                  description="YouTube video veya uzun format içerik"
                />
              </div>

              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Image" size={18} color="var(--color-secondary)" />
                  <h3 className="font-semibold text-sm">Story Paylaşımı</h3>
                </div>
                <Input
                  type="number"
                  placeholder="Örn: 1500"
                  value={formData?.pricing?.story}
                  onChange={(e) => handlePricingChange('story', e?.target?.value)}
                  description="Instagram/Facebook 24 saatlik story"
                />
              </div>

              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="FileText" size={18} color="var(--color-accent)" />
                  <h3 className="font-semibold text-sm">Post Paylaşımı</h3>
                </div>
                <Input
                  type="number"
                  placeholder="Örn: 3000"
                  value={formData?.pricing?.post}
                  onChange={(e) => handlePricingChange('post', e?.target?.value)}
                  description="Kalıcı feed paylaşımı"
                />
              </div>

              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Film" size={18} color="var(--color-success)" />
                  <h3 className="font-semibold text-sm">Reels / TikTok</h3>
                </div>
                <Input
                  type="number"
                  placeholder="Örn: 2500"
                  value={formData?.pricing?.reel}
                  onChange={(e) => handlePricingChange('reel', e?.target?.value)}
                  description="Kısa format video içeriği"
                />
              </div>
            </div>

            <div className="mt-4">
              <Select
                label="Teslimat Süresi"
                placeholder="Ortalama teslimat sürenizi seçin"
                options={deliveryTimeOptions}
                value={formData?.deliveryTime}
                onChange={(value) => handleInputChange('deliveryTime', value)}
                description="İçeriği ne kadar sürede teslim edebilirsiniz?"
                required
              />
            </div>
          </div>

          {/* Portfolio Upload */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Briefcase" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Portfolyo</h2>
                <p className="text-sm text-muted-foreground">Önceki çalışmalarınızı ve içeriklerinizi yükleyin</p>
              </div>
            </div>

            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-secondary transition-smooth cursor-pointer">
              <Icon name="Upload" size={32} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium mb-1">Medya dosyalarını yüklemek için tıklayın</p>
              <p className="text-xs text-muted-foreground">Resim, video veya bağlantılar ekleyebilirsiniz</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-3 md:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="md:w-auto"
            >
              İptal
            </Button>
            <Button
              type="submit"
              iconName="Check"
              className="md:w-auto"
            >
              Profili Kaydet
            </Button>
          </div>
        </form>
      </main>

      <MobileNav userRole="influencer" />
    </div>
  );
};

export default InfluencerProfileCreation;